import {
  apiRouteOf,
  BUILD_ID_HEADER,
  CLIENT_EVENTS_PATH,
  CLIENT_VITALS_SAMPLE_RATE,
  type ClientEventBatch,
  durationBucket,
  stackFramesOf,
} from "@esi/shared";
import { useEffect } from "react";
import { type Metric, onCLS, onINP, onLCP, onTTFB } from "web-vitals";
import { currentRouteName } from "../router.tsx";
import { BUILD_ID } from "../shell/build-refresh.ts";
import { type Collector, createCollector, type SendBatch } from "./collector.ts";
import { clientContext } from "./environment.ts";

/**
 * The SPA's client telemetry (openspec change `client-telemetry`): one collector per page load,
 * installed once by `main.tsx` (StrictMode's double effects never install it twice). Its events
 * go to `/api/client-events` on this origin only, with raw `fetch` (never `apiFetch`: telemetry
 * must not trigger session checks, retries or reloads, nor report on itself).
 */

/** `fetch` with `keepalive`, or a beacon (no headers: the envelope carries the build). */
export const sendBatch: SendBatch = (batch: ClientEventBatch, { beacon }) => {
  const body = JSON.stringify(batch);
  if (beacon && typeof navigator.sendBeacon === "function") {
    try {
      // A JSON blob: the endpoint takes `application/json` only.
      if (
        navigator.sendBeacon(CLIENT_EVENTS_PATH, new Blob([body], { type: "application/json" }))
      ) {
        return;
      }
    } catch {
      // Some browsers refuse a non-form beacon body: `fetch` below.
    }
  }
  void fetch(CLIENT_EVENTS_PATH, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "content-type": "application/json", [BUILD_ID_HEADER]: BUILD_ID },
    body,
  }).catch(() => {
    // Lost telemetry is only lost telemetry.
  });
};

/** The current page's route name, never its path. */
const currentRoute = () => {
  try {
    return currentRouteName();
  } catch {
    return "unknown";
  }
};

let collector: Collector | null = null;

function telemetry(): Collector {
  collector ??= createCollector({
    send: sendBatch,
    context: () => clientContext(),
    build: BUILD_ID,
    origin: window.location.origin,
  });
  return collector;
}

/** An error or a rejection's reason as an error event (`name`, `message`, own frames). */
export function errorEvent(
  error: unknown,
  source: "window" | "promise" | "render",
  fallback?: { message?: string; file?: string; line?: number; col?: number },
) {
  const isError =
    error instanceof Error || Object.prototype.toString.call(error) === "[object Error]";
  const e = isError ? (error as Error) : undefined;
  let frames = stackFramesOf(e?.stack, window.location.origin);
  if (frames.length === 0 && fallback?.file) {
    frames = stackFramesOf(
      `at ${fallback.file}:${fallback.line ?? 0}:${fallback.col ?? 0}`,
      window.location.origin,
    );
  }
  // A thrown non-error (a string, an object) may hold anything: its type only.
  const name = e ? e.name : source === "promise" ? "UnhandledRejection" : "Error";
  const message = e ? e.message : (fallback?.message ?? undefined);
  return {
    kind: "error" as const,
    source,
    route: currentRoute(),
    name,
    ...(typeof message === "string" && { message }),
    frames,
    count: 1,
  };
}

/** Records an error (also used by React's root error hooks). Never throws. */
export function reportError(error: unknown, source: "window" | "promise" | "render"): void {
  try {
    telemetry().record(errorEvent(error, source));
    // A render error may have unmounted the tree whose effects would have armed the collector.
    if (source === "render") {
      void resolvePending();
    }
  } catch {
    // Never let telemetry break the page.
  }
}

/**
 * `telemetry.clientEvents` as the server states it: the signed-out session answer's, or the
 * signed-in user's `/api/me`; undefined when unclear (an operator, an error, an old API).
 */
export async function policyFromServer(
  fetcher: typeof fetch = fetch,
): Promise<boolean | undefined> {
  try {
    const init = { headers: { [BUILD_ID_HEADER]: BUILD_ID }, credentials: "same-origin" as const };
    const session = (await (await fetcher("/api/auth/session?probe=1", init)).json()) as {
      authenticated?: boolean;
      clientTelemetry?: unknown;
      principal?: { type?: string };
    };
    let enabled: unknown;
    if (session.authenticated === false) {
      enabled = session.clientTelemetry;
    } else if (session.principal?.type === "user") {
      enabled = ((await (await fetcher("/api/me", init)).json()) as { clientTelemetry?: unknown })
        .clientTelemetry;
    }
    return typeof enabled === "boolean" ? enabled : undefined;
  } catch {
    return undefined;
  }
}

/** How long after page load the collector waits for the app's own answer before asking. */
export const POLICY_FALLBACK_MS = 15_000;

let resolving: Promise<void> | null = null;

/**
 * The policy when no view supplied it (a page outside the app's session handling, or a tree
 * that crashed before its effects ran): the session probe (`?probe=1`, which neither slides nor
 * rotates the session), then `/api/me` when signed in. Raw `fetch`, like the transport. Nothing
 * is armed on an unclear answer: the events keep waiting.
 */
export function resolvePending(fetcher: typeof fetch = fetch): Promise<void> {
  if (telemetry().state !== "pending") {
    return Promise.resolve();
  }
  resolving ??= policyFromServer(fetcher)
    .then((enabled) => {
      // The app may have answered meanwhile: its answer stands.
      if (enabled !== undefined && telemetry().state === "pending") {
        setClientTelemetry(enabled);
      }
    })
    .finally(() => {
      resolving = null;
    });
  return resolving;
}

/**
 * A failed API call from `apiFetch` (5xx, a network error or a timeout; never 4xx), by the API's
 * route template and a duration class.
 */
export function reportFailedRequest(input: {
  method: string;
  path: string;
  failure: number | "network" | "timeout";
  durationMs: number;
}): void {
  try {
    const method = input.method.toUpperCase();
    if (
      input.path === CLIENT_EVENTS_PATH ||
      !["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"].includes(method)
    ) {
      return;
    }
    telemetry().record({
      kind: "request",
      route: currentRoute(),
      method: method as "GET",
      apiRoute: apiRouteOf(input.path),
      failure: input.failure,
      durationBucket: durationBucket(input.durationMs),
    });
  } catch {
    // Never let telemetry break a request.
  }
}

/** Reports Core Web Vitals for a sampled share of page loads. */
export function watchVitals(
  sample: () => boolean = () => Math.random() < CLIENT_VITALS_SAMPLE_RATE,
  observers: ((report: (metric: Metric) => void) => void)[] = [onLCP, onINP, onCLS, onTTFB],
): boolean {
  if (!sample()) {
    return false;
  }
  const report = (metric: Metric) =>
    telemetry().record({
      kind: "vital",
      route: currentRoute(),
      name: metric.name as "LCP",
      value: Math.max(0, Math.min(metric.value, 600_000)),
    });
  for (const observe of observers) {
    observe(report);
  }
  return true;
}

let installed = false;

/**
 * Once per page load: the boot event, the window's error hooks, vitals (sampled) and the flush
 * when the page is hidden. Nothing is sent until {@link setClientTelemetry} says on.
 */
export function installClientTelemetry(): void {
  if (installed) {
    return;
  }
  installed = true;
  const c = telemetry();
  c.record({ kind: "boot", route: currentRoute() });
  window.addEventListener("error", (event) => {
    try {
      c.record(
        errorEvent(event.error, "window", {
          message: event.message,
          file: event.filename,
          line: event.lineno,
          col: event.colno,
        }),
      );
    } catch {
      // Ignored.
    }
  });
  window.addEventListener("unhandledrejection", (event) => reportError(event.reason, "promise"));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      c.flush(true);
    }
  });
  try {
    watchVitals();
  } catch {
    // Vitals are optional.
  }
  // Pages that never ask (invitation and reset links, the device and consent pages) still report.
  setTimeout(() => void resolvePending(), POLICY_FALLBACK_MS);
}

/** Sends one queued batch now (tests; the collector flushes on its own every 10 s). */
export function flushClientTelemetry(beacon = false): void {
  telemetry().flush(beacon);
}

/**
 * Arms the collector when `telemetry.clientEvents` is on, disarms it when off (from `/api/me`,
 * or the signed-out session answer); undefined: not known yet, events wait.
 */
export function setClientTelemetry(enabled: boolean | undefined): void {
  if (enabled === undefined) {
    return;
  }
  if (enabled) {
    telemetry().arm();
  } else {
    telemetry().disarm();
  }
}

/** {@link setClientTelemetry} as an effect. */
export function useClientTelemetry(enabled: boolean | undefined): void {
  useEffect(() => {
    setClientTelemetry(enabled);
  }, [enabled]);
}
